import { Helmet } from 'react-helmet';
import { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostMessage, DeviceType } from 'spectacle-ui-game-bridge-react';
import classNames from 'classnames';
import { usePreloginContext } from '../../context/PreloginContext';
import AccountUpgraded from '../../components/accountUpgraded/accountUpgraded';
import Build from '../../components/build/build';
import { getPath } from '../../config';
import Debug from '../../components/debug/debug';
import flipBool from '../../utils/flipBool';
import GuestLogin from '../../components/guestLogin/guestLogin';
import SignIn from '../../components/signIn/signIn';
import TwoFactorAuth from '../../components/twoFactorAuth/twoFactorAuth';
import useContentForBuild from '../../hooks/useContentForBuild';
import useDebouncedDlogFunnel from '../../hooks/useDebouncedDlogFunnel';
import useDlogLoginFunnel, { LoginFunnelAction } from '../../hooks/useDlogLoginFunnel';
import VideoPlayer from '../../components/videoPlayer/videoPlayer';
import ErrorStrip from '../../components/errorStrip/errorStrip';
import CaretIcon from '../../icons/CaretIcon';
import Loader from '../../components/loader/loader';
import './login.sass';

export default function Login() {
  const navigate = useNavigate();
  const { accountRegistrationStatus, hasValidToken, hasAnExistingSoftAccount, device_os, hasUserAccountUpgraded, hasCreatedSoftAccount, systemCountry, isGuestLoginErrorShown, setIsGuestLoginErrorShown } = usePreloginContext();
  const openUrl = usePostMessage('openUrl');
  const passPreloginFence = usePostMessage('passPreloginFence');
  const closeWebView = usePostMessage('closeWebView');
  const playAsExistingSoftAccount = usePostMessage('playAsExistingSoftAccount');
  const [isShowingLoader, setIsShowingLoading] = useState<boolean>(false);
  const [isSignInShown, setIsSignInShown] = useState(false);
  const [isGuestLoginShown, setIsGuestLoginShown] = useState(false);
  const [isAccountUpgradeShown, setIsAccountUpgradeShown] = useState(false);
  const [is2faShown, setIs2faShown] = useState(false);
  const [localHasUserAccountUpgraded, setLocalHasUserAccountUpgraded] = useState(hasUserAccountUpgraded);
  const [localHasExistingSoftAccount, setLocalHasExistingSoftAccount] = useState(hasAnExistingSoftAccount);
  const dlog = useDlogLoginFunnel();
  const dlogVideoPlayed = useDebouncedDlogFunnel({
    phase: 'Video_Player_Active',
    action: 'Video_Started',
    targets: 'Login',
    resume: false,
  });
  const dlogVideoLooped = useDebouncedDlogFunnel({
    phase: 'Video_Player_Active',
    action: 'Video_Started',
    targets: 'Login',
    resume: true,
  });
  const dlogVideoStuttering = useDebouncedDlogFunnel({
    phase: 'Video_Player_Active',
    action: 'Video_Stuttering',
    targets: 'Login',
  });
  const [content] = useContentForBuild();

  const handleUrl = useCallback(
    (e: any) => {
      e.preventDefault();
      const href = e.target.href;

      openUrl(href);
    },
    [openUrl]
  );

  const toggleSignIn = () => setIsSignInShown(flipBool);
  const hideSignIn = () => setIsSignInShown(false);
  const toggleSetIsAccountUpgradeShown = () => setIsAccountUpgradeShown(flipBool);

  const handleShowLogin = () => {
    setIsAccountUpgradeShown(false);
    setIsSignInShown(true);
  };

  const toggleGuestLogin = useCallback(() => {
    if (localHasUserAccountUpgraded) {
      setIsAccountUpgradeShown(true);
      return;
    }

    dlog({
      phase: 'Login_Screen',
      action: 'Click',
      targets: 'Guest',
    });

    if (localHasExistingSoftAccount) {
      playAsExistingSoftAccount();
      setIsShowingLoading(true);

      switch (accountRegistrationStatus) {
        case 'AccountRegistrationSuccess':
          if (hasValidToken) {
            setIsShowingLoading(false);
            if (content?.enablePostLoginMessages) {
              navigate('/post-login');
            } else {
              closeWebView();
            }
          }
          break;
        case 'AccountRegistrationFailure':
          setIsGuestLoginErrorShown(true);
          setIsShowingLoading(false);
          break;
      }

      return;
    }

    setIsGuestLoginShown(!isGuestLoginShown);
  }, [isGuestLoginShown, closeWebView, localHasExistingSoftAccount, playAsExistingSoftAccount, dlog, localHasUserAccountUpgraded, content, navigate, hasValidToken, accountRegistrationStatus, setIsGuestLoginErrorShown]);

  const toggle2fa = useCallback(() => {
    setIs2faShown(!is2faShown);
  }, [is2faShown]);

  // If valid token
  useEffect(() => {
    if (hasValidToken === true && content) {
      passPreloginFence();
      if (content?.enablePostLoginMessages) {
        navigate('/post-login');
      } else {
        closeWebView();
      }
    }
  }, [hasValidToken, passPreloginFence, closeWebView, content, navigate]);

  useEffect(() => {
    // Show 2FA Modal
    if (accountRegistrationStatus === 'AccountRegistrationNeedsOTP') {
      setIs2faShown(true);
      setIsSignInShown(false);
    }
  }, [accountRegistrationStatus]);

  const handleRegisterClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    dlog({
      phase: 'Login_Screen',
      action: 'Click',
      targets: 'Sign_Up',
    });
    handleUrl(event);
  };

  useEffect(() => {
    dlog({
      phase: 'Login_Screen',
      action: 'Render',
    });
  }, [dlog]);

  useEffect(() => {
    if (isSignInShown) {
      dlog({
        phase: 'Login_Screen',
        action: 'Click',
        targets: 'Sign_In',
      });
    }
  }, [isSignInShown, dlog]);

  useEffect(() => {
    if (accountRegistrationStatus === 'AccountRegistrationFailure') {
      setIsGuestLoginErrorShown(true);
    }
  }, [accountRegistrationStatus, setIsGuestLoginErrorShown]);

  const showGuestLogin = content?.enableGuestLogin && !localHasUserAccountUpgraded;
  const isFirstTimeLogin = !localHasExistingSoftAccount && !localHasUserAccountUpgraded && content?.enableLoginBigPlay;

  return (
    <>
      <Helmet>
        <title>Login</title>
      </Helmet>
      <div className="login__bg">
        <VideoPlayer
          src={getPath('video_login')}
          muted
          autoPlay
          loop
          cover
          volume={1}
          controls={false}
          onPlay={(event, looped) => (looped ? dlogVideoLooped() : dlogVideoPlayed())}
          onWaitingFirstTime={dlogVideoStuttering}
          onFinishedFirstPlaythrough={(event, stutterCount) =>
            dlog({
              phase: 'Video_Player_Active',
              action: `Video_Stuttered:${stutterCount}` as LoginFunnelAction,
              targets: 'Login',
            })
          }
        />
      </div>
      <div className="login__mask"></div>
      <img src={getPath('wzm_logo')} alt="Call of Duty: Warzone Mobile" className="logo" />
      {content && (
        <>
          {isFirstTimeLogin ? (
            <>
              <div className="login__transport">
                <div className="login__container login__container--full">
                  <div className="login__grid login__grid--3-col">
                    <div>
                      <p className="login__grid-cross">{content.loginCrossProgression}</p>
                      <div className="login__grid login__grid--2-col">
                        <button className="cta cta--full cta--cool" onClick={toggleSignIn}>
                          {content.signInCta}
                        </button>
                        <a className="cta cta--full cta--cool" onClick={handleRegisterClick} href={content.urlRegister}>
                          {content.registerCta}
                        </a>
                      </div>
                    </div>
                    <div className="login__main_cta">
                      <button className="cta cta--play" onClick={toggleGuestLogin}>
                        <CaretIcon />
                        Play
                        <CaretIcon />
                      </button>
                    </div>
                    <div className="text-right login__grid-item---align-bottom">
                      <div className="login__build-small">
                        {content.loginBuild} <Build />
                      </div>
                    </div>
                    <div className="login__grid-item-span-3">{device_os === DeviceType.iOS && <div className="login__notice">{content.loginNotice}</div>}</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="login__transport">
              <div className="login__container">
                <div className={classNames('login__grid', showGuestLogin ? 'login__grid--3-col' : 'login__grid--2-col')}>
                  <div className="login__grid-item-span-2">
                    <div className="login__tip">{content.loginTip}</div>
                  </div>
                  <div></div>
                  <div className="login__grid-item-span-2">
                    <div className="flourish" />
                    {content.loginAccountTitle}
                    <div className="flourish" />
                  </div>
                  {showGuestLogin && <div />}
                  <div>
                    <button className="cta cta--full cta--cool" onClick={toggleSignIn} disabled={isShowingLoader}>
                      {content.signInCta}
                    </button>
                  </div>
                  <div>
                    <a className="cta cta--full cta--cool" onClick={handleRegisterClick} href={content.urlRegister}>
                      {content.registerCta}
                    </a>
                  </div>
                  {showGuestLogin && (
                    <div>
                      <button className="cta cta--full cta--nowrap" onClick={toggleGuestLogin} disabled={isShowingLoader}>
                        {localHasExistingSoftAccount ? content.guestSignInContinue : content.guestSignInCta}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="login__container">{device_os === DeviceType.iOS && <div className="login__notice">{content.loginNotice}</div>}</div>
              <div className="login__build">
                {content.loginBuild} <Build />
              </div>
            </div>
          )}
        </>
      )}
      <ErrorStrip isShown={isGuestLoginErrorShown} msg={content?.guestLoginFailed} displayTime={3000} onHide={() => setIsGuestLoginErrorShown(false)} />
      <TwoFactorAuth content={content} isShown={is2faShown} toggle={toggle2fa} />
      <SignIn content={content} isShown={isSignInShown} hide={hideSignIn} handleUrl={handleUrl} />
      <AccountUpgraded content={content} isShown={isAccountUpgradeShown} toggle={toggleSetIsAccountUpgradeShown} onShowLogin={handleShowLogin} />
      {showGuestLogin && <GuestLogin content={content} isShown={isGuestLoginShown} toggleIsShown={toggleGuestLogin} />}
      <Loader isShown={isShowingLoader} />
      <Debug
        key="debug"
        enabled={!!content?.showDeveloperOverlay}
        vars={{
          isGuestLoginErrorShown,
          accountRegistrationStatus,
          isGuestLoginShown,
          isAccountUpgradeShown,
          isSignInShown,
          hasAnExistingSoftAccount,
          localHasExistingSoftAccount,
          localHasUserAccountUpgraded,
          hasCreatedSoftAccount,
          systemCountry,
          hasValidToken,
        }}
      >
        <button
          onClick={() => {
            closeWebView();
          }}
        >
          Close Webview
        </button>
        <button
          onClick={() => {
            window.location.reload();
          }}
        >
          reload
        </button>
        <button
          onClick={() => {
            setLocalHasUserAccountUpgraded((hasUpgraded) => !hasUpgraded);
            setLocalHasExistingSoftAccount((hasExisting) => !hasExisting);
          }}
        >
          Toggle User Account Has Upgraded
        </button>
        <button onClick={() => setIsGuestLoginErrorShown((prev) => !prev)}>Toggle Inline Error</button>
      </Debug>
    </>
  );
}
